import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageWithSupportTicketId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import {
  supportTicketListCountFields,
  type SupportTicketListItemResponse,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import {
  encodeSupportTicketSorts,
  type SupportTicketSort,
} from "@redotech/effect-api-shared/schema/query-options";
import { encodeSupportTicketFilterGroup } from "@redotech/effect-api-shared/schema/support-ticket-filters";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { and, eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import type {
  PresenceCollection,
  SupportTicketPresenceRecord,
} from "../realtime/support-ticket-presence-collection";
import type {
  SupportTicketListCollectionWithLoader,
  SupportTicketPageMetadata,
} from "./support-ticket-list-collection";
import {
  supportTicketDetailQueryKey,
  supportTicketsPageQueryKey,
} from "./support-ticket-query-keys";
import { filterGroupToTanstackExpression } from "./support-ticket-tanstack-expressions";
import type {
  MessagesCollection,
  SupportTicketThreadCollectionItem,
  ThreadsCollection,
  TimelineEventsCollection,
} from "./support-ticket-transcript-collections";

const STALE_TIME = 5 * 60 * 1000;
const SUPPORT_TICKET_SWITCH_LIVE_QUERY_GC_TIME = 60 * 1000;

export function orderSupportTicketsByPageIds(
  supportTickets: readonly SupportTicketListItemResponse[],
  supportTicketIds: readonly string[],
): SupportTicketListItemResponse[] {
  const orderById = new Map(
    supportTicketIds.map((supportTicketId, index) => [supportTicketId, index]),
  );

  return [...supportTickets].sort((a, b) => {
    const aOrder = orderById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = orderById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });
}

export function mergeSupportTicketDetailForCollection(
  detail: SupportTicketListItemResponse,
  existing: SupportTicketListItemResponse | undefined,
): SupportTicketListItemResponse {
  if (!existing) {
    return detail;
  }

  const merged = { ...detail };
  for (const field of supportTicketListCountFields) {
    merged[field] = existing[field];
  }
  if (merged.summary === null && existing.summary !== null) {
    merged.summary = existing.summary;
  }
  if (merged.emailSubject === null && existing.emailSubject !== null) {
    merged.emailSubject = existing.emailSubject;
  }
  if (existing.searchHighlights) {
    merged.searchHighlights = existing.searchHighlights;
  }
  return merged;
}

export interface SupportTicketDataCollections {
  supportTicketsList: SupportTicketListCollectionWithLoader;
  threads: ThreadsCollection;
  messages: MessagesCollection;
  presence: PresenceCollection;
  timelineEvents: TimelineEventsCollection;
}

function supportTicketPageMetadataQuery({
  encodedFilterGroup,
  encodedSorts,
  page,
  pageSize,
  supportTicketsList,
}: {
  encodedFilterGroup: string | undefined;
  encodedSorts: string;
  page: number;
  pageSize?: number;
  supportTicketsList: SupportTicketListCollectionWithLoader;
}) {
  return queryOptions({
    queryKey: supportTicketsPageQueryKey({
      encodedFilterGroup,
      encodedSorts,
      page,
      pageSize,
      scope: supportTicketsList.queryScope,
    }),
    queryFn: async (): Promise<SupportTicketPageMetadata> => {
      const result = await supportTicketsList.fetchPage(
        page,
        encodedFilterGroup,
        pageSize,
        encodedSorts,
      );
      return {
        nextCursor: result.nextCursor,
        supportTicketIds: result.supportTicketIds,
        totalCount: result.totalCount,
      };
    },
    staleTime: STALE_TIME,
  });
}

export function useSupportTicketsData({
  page,
  collections,
  filterGroup,
  sorts,
  pageSize,
}: {
  page: number;
  collections: SupportTicketDataCollections;
  filterGroup: FilterGroup;
  sorts: readonly SupportTicketSort[];
  pageSize?: number;
}): {
  data: SupportTicketListItemResponse[];
  totalCount: number;
  isLoading: boolean;
} {
  const encodedFilterGroup = filterGroup
    ? encodeSupportTicketFilterGroup(filterGroup)
    : undefined;
  const encodedSorts = encodeSupportTicketSorts(sorts);
  const supportTicketsList = collections.supportTicketsList;

  const { data: pageMetadata, isLoading: isFetching } = useQuery(
    supportTicketPageMetadataQuery({
      encodedFilterGroup,
      encodedSorts,
      page,
      pageSize,
      supportTicketsList,
    }),
  );

  const supportTicketIds = useMemo(
    () => pageMetadata?.supportTicketIds ?? [],
    [pageMetadata?.supportTicketIds],
  );
  const idsKey = supportTicketIds.join(",");

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ supportTicket: supportTicketsList.collection })
        .where(({ supportTicket }) => {
          const idFilter = inArray(supportTicket.id, supportTicketIds);
          const filterExpr = filterGroup
            ? filterGroupToTanstackExpression(filterGroup, supportTicket)
            : null;
          return filterExpr ? and(idFilter, filterExpr) : idFilter;
        }),
    [idsKey, encodedFilterGroup, supportTicketsList.collection],
  );
  const orderedData = useMemo(
    () => orderSupportTicketsByPageIds(liveResult.data, supportTicketIds),
    [liveResult.data, supportTicketIds],
  );

  useEffect(() => {
    void supportTicketsList.prefetchPagesAfter({
      filterGroup: encodedFilterGroup,
      nextCursor: pageMetadata?.nextCursor,
      page,
      pageSize,
      sorts: encodedSorts,
      totalCount: pageMetadata?.totalCount,
    });
  }, [
    page,
    pageSize,
    pageMetadata?.nextCursor,
    pageMetadata?.totalCount,
    encodedFilterGroup,
    encodedSorts,
    supportTicketsList,
  ]);

  return {
    data: orderedData,
    totalCount: pageMetadata?.totalCount ?? 0,
    isLoading: isFetching || liveResult.isLoading,
  };
}

export function useSupportTicketData({
  supportTicketId,
  collections,
  fetchSupportTicket,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
  fetchSupportTicket: (
    supportTicketId: SupportTicketId,
  ) => Promise<SupportTicketListItemResponse>;
}): { data: SupportTicketListItemResponse | undefined; isLoading: boolean } {
  const liveResult = useLiveQuery(
    {
      query: (q) =>
        q
          .from({ supportTicket: collections.supportTicketsList.collection })
          .where(({ supportTicket }) => eq(supportTicket.id, supportTicketId))
          .findOne(),
      gcTime: SUPPORT_TICKET_SWITCH_LIVE_QUERY_GC_TIME,
    },
    [supportTicketId, collections.supportTicketsList.collection],
  );

  const { isLoading: isFetchingSingle } = useQuery({
    queryKey: supportTicketDetailQueryKey(
      supportTicketId,
      collections.supportTicketsList.queryScope,
    ),
    queryFn: async () => {
      const supportTicket = await fetchSupportTicket(supportTicketId);
      const existing =
        collections.supportTicketsList.collection.get(supportTicketId);
      collections.supportTicketsList.writeItem(
        mergeSupportTicketDetailForCollection(supportTicket, existing),
      );
      return supportTicket;
    },
    enabled: liveResult.data === undefined,
    staleTime: STALE_TIME,
  });

  return {
    data: liveResult.data,
    isLoading: liveResult.isLoading || isFetchingSingle,
  };
}

export function useChannelThreadsData({
  supportTicketId,
  collections,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
}): { data: SupportTicketThreadCollectionItem[]; isLoading: boolean } {
  const liveResult = useLiveQuery(
    {
      query: (q) =>
        q
          .from({ thread: collections.threads })
          .where(({ thread }) => eq(thread.supportTicketId, supportTicketId))
          .orderBy(({ thread }) => thread.createdAt, "asc"),
      gcTime: SUPPORT_TICKET_SWITCH_LIVE_QUERY_GC_TIME,
    },
    [supportTicketId, collections.threads],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export function useSupportTicketMessagesData({
  supportTicketId,
  collections,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
}): { data: MessageWithSupportTicketId[]; isLoading: boolean } {
  const liveResult = useLiveQuery(
    {
      query: (q) =>
        q
          .from({ message: collections.messages })
          .where(({ message }) => eq(message.supportTicketId, supportTicketId))
          .orderBy(({ message }) => message.sentAt, "asc"),
      gcTime: SUPPORT_TICKET_SWITCH_LIVE_QUERY_GC_TIME,
    },
    [supportTicketId, collections.messages],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export type QueryRows<T> =
  | { readonly status: "loading"; readonly rows: readonly T[] }
  | { readonly status: "ready"; readonly rows: readonly T[] };

export function queryRowsState<T>({
  isLoading,
  rows,
}: {
  isLoading: boolean;
  rows: readonly T[];
}): QueryRows<T> {
  return { rows, status: isLoading ? "loading" : "ready" };
}

export function getSingleSupportTicketLoadingState({
  messages,
  supportTicket,
  supportTicketLoading,
  threads,
}: {
  messages: QueryRows<unknown>;
  supportTicket: SupportTicketListItemResponse | undefined;
  supportTicketLoading: boolean;
  threads: QueryRows<unknown>;
}): {
  isLoading: boolean;
  supportTicketLoading: boolean;
  transcriptLoading: boolean;
} {
  const isSupportTicketLoading =
    supportTicketLoading && supportTicket === undefined;
  const isTranscriptLoading =
    isRowsQueryInitiallyLoading(threads) ||
    isRowsQueryInitiallyLoading(messages);

  return {
    isLoading: isSupportTicketLoading || isTranscriptLoading,
    supportTicketLoading: isSupportTicketLoading,
    transcriptLoading: isTranscriptLoading,
  };
}

function isRowsQueryInitiallyLoading(queryRows: QueryRows<unknown>): boolean {
  return queryRows.status === "loading" && queryRows.rows.length === 0;
}

export function useThreadMessagesData({
  supportTicketId,
  threadIds,
  collections,
}: {
  supportTicketId: SupportTicketId;
  threadIds: ChannelThreadId | readonly ChannelThreadId[];
  collections: SupportTicketDataCollections;
}): { data: Message[]; isLoading: boolean } {
  const threadIdList = useMemo(
    () => (Array.isArray(threadIds) ? [...threadIds] : [threadIds]),
    [threadIds],
  );

  const result = useLiveQuery(
    {
      query: (q) =>
        q
          .from({ message: collections.messages })
          .where(({ message }) =>
            and(
              eq(message.supportTicketId, supportTicketId),
              inArray(message.channelThreadId, threadIdList),
            ),
          )
          .orderBy(({ message }) => message.sentAt, "asc"),
      gcTime: SUPPORT_TICKET_SWITCH_LIVE_QUERY_GC_TIME,
    },
    [supportTicketId, threadIdList, collections.messages],
  );

  return { data: result.data, isLoading: result.isLoading };
}

export function useSupportTicketTimelineEventsData({
  supportTicketId,
  collections,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
}): { data: SupportTicketTimelineEventResponse[]; isLoading: boolean } {
  const result = useLiveQuery(
    {
      query: (q) =>
        q
          .from({ event: collections.timelineEvents })
          .where(({ event }) => eq(event.supportTicketId, supportTicketId))
          .orderBy(({ event }) => event.occurredAt, "desc"),
      gcTime: SUPPORT_TICKET_SWITCH_LIVE_QUERY_GC_TIME,
    },
    [supportTicketId, collections.timelineEvents],
  );

  return { data: result.data ?? [], isLoading: result.isLoading };
}

export function useSupportTicketPresenceData({
  supportTicketId,
  collections,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
}): { data: SupportTicketPresenceRecord[] } {
  const result = useLiveQuery(
    (q) =>
      q
        .from({ presence: collections.presence })
        .where(({ presence }) => eq(presence.supportTicketId, supportTicketId)),
    [supportTicketId, collections.presence],
  );

  return { data: result.data };
}

export function useSingleSupportTicketDataCore({
  supportTicketId,
  collections,
  fetchSupportTicket,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
  fetchSupportTicket: (
    supportTicketId: SupportTicketId,
  ) => Promise<SupportTicketListItemResponse>;
}): {
  supportTicket: SupportTicketListItemResponse | undefined;
  threads: SupportTicketThreadCollectionItem[];
  messages: Message[];
  isLoading: boolean;
  supportTicketLoading: boolean;
  transcriptLoading: boolean;
} {
  const supportTicketResult = useSupportTicketData({
    supportTicketId,
    collections,
    fetchSupportTicket,
  });
  const threadsResult = useChannelThreadsData({ supportTicketId, collections });
  const messagesResult = useSupportTicketMessagesData({
    supportTicketId,
    collections,
  });
  const loadingState = getSingleSupportTicketLoadingState({
    messages: queryRowsState({
      isLoading: messagesResult.isLoading,
      rows: messagesResult.data,
    }),
    supportTicket: supportTicketResult.data,
    supportTicketLoading: supportTicketResult.isLoading,
    threads: queryRowsState({
      isLoading: threadsResult.isLoading,
      rows: threadsResult.data,
    }),
  });

  return {
    supportTicket: supportTicketResult.data,
    threads: threadsResult.data,
    messages: messagesResult.data,
    ...loadingState,
  };
}

export { DEFAULT_SUPPORT_TICKET_PAGE_SIZE } from "./support-ticket-list-requests";
